import { jsxs, jsx, Fragment as Fragment$1 } from 'react/jsx-runtime';
import { useCallback, Fragment } from 'react';
import { flexRender } from '@tanstack/react-table';
import classNames from 'classnames';
import { Table } from '../../Table/Table.es.js';
import { AdvancedTableRowSubComponent } from '../AdvancedTableRowSubComponent/AdvancedTableRowSubComponent.es.js';
import { LoadingRows } from './LoadingRows.es.js';

const AdvancedTableBody = ({ loading, onRowClick, applyGrouping, getRowSubComponent, customTableBodyProps, ...table })=>{
    const { options, getRowModel, toggleAllRowsSelected } = table;
    const { enableMultiRowSelection } = options;
    const rows = getRowModel().rows;
    const renderRow = useCallback((row)=>{
        const isMultiRowSelectionEnabled = typeof enableMultiRowSelection === 'boolean' ? enableMultiRowSelection : enableMultiRowSelection?.(row) ?? false;
        return jsxs(Fragment, {
            children: [
                jsx(Table.Body.Row, {
                    className: classNames('row', customTableBodyProps?.className),
                    clickable: onRowClick != null,
                    // TODO: Fix the component to remove eslint error;
                    // fix in: [https://circlepay.atlassian.net/browse/CF-402]
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick: ()=>{
                        if (!isMultiRowSelectionEnabled) {
                            toggleAllRowsSelected?.(false);
                            row.toggleSelected();
                        }
                        onRowClick?.(row);
                    },
                    // no select highlighting for checkbox/multiple
                    selected: row.getIsSelected() && !isMultiRowSelectionEnabled,
                    ...row.getCanExpand() ? {
                        'aria-expanded': row.getIsExpanded(),
                        'aria-level': 1
                    } : {},
                    children: row.getVisibleCells().map((cell)=>jsx(Table.Body.Cell, {
                            className: cell.column.columnDef.meta?.className,
                            role: row.getCanExpand() ? 'gridcell' : 'cell',
                            style: {
                                width: cell.column.getSize()
                            },
                            children: flexRender(cell.column.columnDef.cell, cell.getContext())
                        }, cell.id))
                }),
                jsx(AdvancedTableRowSubComponent, {
                    getRowSubComponent: getRowSubComponent,
                    row: row
                })
            ]
        }, row.id);
    }, [
        customTableBodyProps?.className,
        enableMultiRowSelection,
        onRowClick,
        toggleAllRowsSelected,
        getRowSubComponent
    ]);
    // Display Loading Rows
    if (loading) {
        return jsx(LoadingRows, {
            ...table
        });
    }
    // Display "Grouped" Data Rows
    if (typeof applyGrouping === 'function') {
        /**
         * Track prev applyGrouping value.
         */ let prevGroupValue = null;
        /**
         * Row groupings array.
         */ const rowGroups = [];
        rows.forEach((row)=>{
            /**
             * Calculate value through applyGrouping.
             */ const currentGroupValue = applyGrouping?.(row.original) || null;
            if (currentGroupValue !== prevGroupValue) {
                rowGroups.push({
                    groupId: `${currentGroupValue}${row.id}`,
                    groupName: currentGroupValue ?? '',
                    rows: [
                        row
                    ]
                });
                /**
                 * Override prev group value with newer one.
                 */ prevGroupValue = currentGroupValue;
            } else {
                rowGroups[rowGroups.length - 1].rows.push(row);
            }
        });
        return jsx(Fragment$1, {
            children: rowGroups.map((rowGroup)=>{
                return jsxs(Table.Body, {
                    role: "rowgroup",
                    children: [
                        jsx(Table.Body.Row, {
                            isGroupHeader: true,
                            children: jsx(Table.Body.Cell, {
                                colSpan: rowGroup.rows[0].getVisibleCells().length,
                                children: jsx("div", {
                                    children: rowGroup.groupName
                                })
                            })
                        }),
                        rowGroup.rows.map((row)=>{
                            return renderRow(row);
                        })
                    ]
                }, rowGroup.groupId);
            })
        });
    }
    // Display Default Data Rows
    return jsx(Table.Body, {
        children: rows.map((row)=>renderRow(row))
    });
};

export { AdvancedTableBody };
//# sourceMappingURL=AdvancedTableBody.es.js.map
